import React, {
	useState,
	useEffect,
	useContext,
	useRef
} from 'react';
import {
	useMediaQuery,
	RootRef,
	Popover,
	MenuItem,
	Typography,
	makeStyles
} from '@material-ui/core';
import {
	useTheme
} from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Fido from '../utils/Fido';
import Constants from "../utils/Constants";
import { isJsonEqual } from "../utils/Json";
import DeconstructActivity from '../utils/DeconstructActivity';
import IndividualActivity from "../components/IndividualActivity";
import ShareActivityForm from "../components/ShareActivityForm";
import ShareToBtaDialog from "../components/ShareToBtaDialog";
import ShareToP3Dialog from "../components/ShareToP3Dialog";
import P3Icon from '../assets/p3icon.svg';
import AuthorizedUser from "../utils/AuthorizedUser";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";
import RestoreBanner from "../components/RestoreBanner";
import ScrollableArea from "../components/ScrollableArea";

// TEMP
// ***********************
//import {singleActivity, shareitSingleActivity, shareit2SingleActivity} from "../data/activities.js";
// ***********************

const styles = makeStyles(theme => ({
	shareMenuIcon: {
		height: "1.3rem",
		width: "1.3rem !important",
		marginRight: theme.spacing(1)
	}
}));

// if clicking no action or remove too quickly, cards can get into a weird state SNT-506
const Activity = React.memo(function Activity(props) {
	const theme = useTheme();
	const restoreBannerRef = useRef();
	const authenticatedUser = useContext(AuthorizedUserContext);

	const [deleted, setDeleted] = useState(false);
	const [shareOpen, setShareOpen] = useState(false);
	const [shareMenuOpen, setShareMenuOpen] = useState(false);
	const [shareToBtaOpen, setShareToBtaOpen] = useState(false);
	const [shareToP3Open, setShareToP3Open] = useState(false);
	const [defaultRecipient, setDefaultRecipient] = useState();
	const [bannerShown, setBannerShown] = useState(false);

	let classes = styles(),
		atLeastSM = useMediaQuery(theme.breakpoints.up("sm")),
		{ showSnackbar, moveStatusHandler, careStatusHandler, viewedCallback, activity, fidoError, updateActivityHandler, searchTerm } = props;

	// set deleted
	useEffect(() => {
		if (activity) {
			//console.log("set deleted hook - setting deleted");
			setDeleted(Constants.ACTIVITY.isDeleted(authenticatedUser, activity));

			if (viewedCallback) {
				viewedCallback(activity.id);
			}

			/*if (authenticatedUser && !authenticatedUser.ghosting && activity &&
				(!activity.views || !activity.views.length ||
					!Constants.ACTIVITY.hasViewed(authenticatedUser, activity.views))) {
				viewedFido.dropIt();
				(async () => {
					let data = await viewedFido.fetch(`/activities/${activity.id}/listing-view`, {method: "PUT"})
						.catch(error => showSnackbar(error.message));

					if (data && viewedCallback) {
						viewedCallback(data.id);
					}
				})();
			}*/
		}
	}, [activity, authenticatedUser, showSnackbar, viewedCallback]);


	const handleTranslateClick = () => {
		let fido = new Fido(),
			pathname = props.location.pathname,
			url = `/activities/${activity.id}/translate`,
			msg = 'Translating...',
			successMsg = 'Translated';

		if (authenticatedUser) {
			fido.fetch(url, {
				method: "PUT"
			}, msg, successMsg)
				.then((data) => {
					updateActivityHandler(data);
				})
				.catch(error => showSnackbar(error.message));
		} else {
			fido.fetch(`${pathname}/translate`, {
				method: "PUT"
			}, msg, successMsg)
				.then((data) => {
					updateActivityHandler(data);
				})
				.catch(error => showSnackbar(error.message));
		}
	};

	const addCommentHandler = (formVals) => {
		let fido = new Fido();
		fido.fetch(`/activities/${activity.id}`, {
			method: "POST",
			body: JSON.stringify({ activity: formVals })
		}, 'Posting Comment...', 'Comment Posted.')
			.then(data => {
				updateActivityHandler(data);
			})
			.catch(error => showSnackbar(error.message));
	};

	const noActionNeeded = () => {
		let { onAnimateOutHandler, postActionHandler, currentStatus, showRemoved, history } = props,
			shouldAnimateOut = currentStatus !== Constants.ACTIVITY.STATUSES.RESOLVED.ID || !showRemoved;

		if (shouldAnimateOut) {
			onAnimateOutHandler(activity.id);
		}

		let poodle = new Fido();
		poodle.fetch(`/activities/${activity.id}/delete`, {
			method: "PUT",
		}, {
			message: 'Removing...'/*,
			action: (<Button size="small" onClick={event => undelete(event, activity.id)} color="primary">Undo</Button>)*/
		}, 'Removed.')
			.then(result => {
				if (result && result.activity) {
					// backup if on mobile
					if (!atLeastSM) {
						history.replace("/inbox");
					}

					if (!shouldAnimateOut && atLeastSM) {
						updateActivityHandler(result);
					}
				}

				// update the esclated counter 
				const numberCircleElement = document.querySelector('#eslcatedCount');
				if (numberCircleElement) {
					let fidoo = new Fido();
					fidoo.fetch('/esclatedAlerts/esclatedCount', {
						method: "GET"
					})
						.then((data) => {
							numberCircleElement.innerHTML = data.count;
						});
				}

				if (shouldAnimateOut && postActionHandler) {
					console.log("deleted, calling postActionHandler: ", activity.id);
					return postActionHandler(activity.id);
				}
			})
			.catch(error => {
				//console.log("EEEERRRRROOOOORRRRR: ", error);
				showSnackbar(error.message);
				if (shouldAnimateOut && postActionHandler) {
					return postActionHandler(activity.id, error);
				}
			});
	};

	const saveOrUnsave = (action, msg, confirmMsg) => {
		let poodle = new Fido(),
			{ history, currentStatus, onAnimateOutHandler, postActionHandler } = props,
			shouldProcessNext = action === "unsave" && currentStatus === Constants.ACTIVITY.CATEGORIES.SAVED.FILTER.ID;

		if (shouldProcessNext) {
			onAnimateOutHandler(activity.id);
		}

		poodle.fetch(`/activities/${activity.id}/${action}`, {
			method: "PUT",
		}, msg, confirmMsg)
			.then(data => {
				if (shouldProcessNext) {
					history.replace("/inbox");
					if (postActionHandler) {
						return postActionHandler(activity.id);
					}
				} else {
					updateActivityHandler(data);
				}
			})
			.catch(error => {
				props.showSnackbar(error.message);
				if (postActionHandler) {
					return postActionHandler(activity.id, error);
				}
			});
	};

	const restoreAndSaveHandler = () => {
		let { currentStatus, onAnimateOutHandler, postActionHandler } = props,
			id = activity && activity.id,
			// activity is NOT deconstructed here (in state)
			animateOut = currentStatus === Constants.ACTIVITY.STATUSES.RESOLVED.ID
				&& activity.status != null && activity.status.status !== Constants.ACTIVITY.STATUSES.RESOLVED.ID;

		if (id) {
			let poodle = new Fido();
			poodle.fetch(`/activities/${id}/undelete`, {
				method: "PUT",
			}, 'Restoring...', 'Restored.')
				.then(data => {
					if (!activity.flagged) {
						data.activity.flagged = true;
						props.showSnackbar(false);
						saveOrUnsave('save', 'Saving...', 'Saved.');
					} else {
						updateActivityHandler(data);
					}

					if (animateOut) {
						onAnimateOutHandler(activity.id);
						if (postActionHandler) {
							return postActionHandler(activity.id);
						}
					}
				})
				.catch(error => {
					props.showSnackbar(error.message);
					if (postActionHandler) {
						return postActionHandler(id, error);
					}
				});
		}
	};

	const save = () => {
		let act = activity;
		if (!act.flagged) {
			if (deleted) {
				restoreAndSaveHandler();
			} else {
				saveOrUnsave('save', 'Saving...', 'Saved.');
				act.flagged = !act.flagged;
				updateActivityHandler(act);
			}
		} else {
			saveOrUnsave('unsave', 'Removing from Saved...', 'Removed from Saved.');
			act.flagged = !act.flagged;
			updateActivityHandler(act);
		}
	};

	const share = (e) => {
		setShareMenuOpen(e.currentTarget);
	};

	const handleShareMenuClose = () => {
		setShareMenuOpen(false);
	};

	const onShareItemClicked = (type) => {
		switch (type) {
			case Constants.ACTIVITY.SHARE_TYPES.EMAIL:
			case Constants.ACTIVITY.SHARE_TYPES.TEXT_MSG:
				setShareOpen(true);
				setDefaultRecipient(type);
				break;
			case Constants.ACTIVITY.SHARE_TYPES.REPORT_TO_BTA:
				setShareToBtaOpen(true);
				break;
			case Constants.ACTIVITY.SHARE_TYPES.REPORT_TO_P3:
				setShareToP3Open(true);
				break;
			default:
				break;
		}
		handleShareMenuClose();
	};

	const handleShareDialogClose = () => {
		setDefaultRecipient(null);
		setShareOpen(false);
	};

	const handleShareSubmit = (activityId, values) => {
		handleShareDialogClose();

		let poodle = new Fido();
		poodle.fetch(`/activities/${activity.id}/share`, {
			method: "PUT",
			body: JSON.stringify(values)
		}, 'Sharing...', 'Shared.')
			.then(data => {
				updateActivityHandler(data);
			})
			.catch(error => props.showSnackbar(error.message));
	};

	const handleRateSubmit = (rating) => {
		if (rating && Object.keys(rating).length) {
			// using 0 as unanswered; allowed by DB
			rating.valuable = rating.valuable !== null && rating.valuable || 0;
			let poodle = new Fido();
			poodle.fetch(`/activities/${activity.id}/rate`, {
				method: "PUT",
				body: JSON.stringify(rating)
			}, 'Rating...', 'Rated.')
				.then(data => {
					if (!Constants.ACTIVITY.isDeleted(authenticatedUser, data.activity)) {
						updateActivityHandler(data);
					}
				})
				.catch(error => props.showSnackbar(error.message));
		}
	};

	const onBannerEntered = () => {
		setBannerShown(true);
	};

	const onBannerExited = () => {
		setBannerShown(false);
	};

	const suppressRateHandler = (isChecked) => {
		let hanSolo = new Fido();
		hanSolo.fetch(`/users/${authenticatedUser.id}/suppress-rate`, {
			method: "PUT",
			body: JSON.stringify({ suppressRate: isChecked })
		})
			.then(() => {
				props.updateUser(Object.assign(authenticatedUser, { noRateOnDelete: + isChecked })); // + is unary operator, converts boolean to int
			})
			.catch((error) => props.showSnackbar(error.message));
	};

	const shareToBta = (notesObj) => {
		setShareToBtaOpen(false);
		if (notesObj !== false && notesObj !== undefined) {
			let fido = new Fido();
			fido.fetch(`/activities/${activity.id}/report-to-bta`, {
				method: "POST",
				body: JSON.stringify({ report: notesObj })
			}, 'Sending to Case Manager ...', 'Sent to Case Manager Queue.')
				.then(data => {
					if (data && data.activity && !isJsonEqual(data.activity, activity)) {
						updateActivityHandler(data);
					}
				})
				.catch(error => showSnackbar(error.message));
		}
	};

	const shareToP3 = (notesObj) => {
		setShareToP3Open(false);
		if (notesObj !== false && notesObj !== undefined) {
			let fido = new Fido();
			let topicsArray = activity.post_matches && activity.post_matches.length ? activity.post_matches.map(t => t.library_topic ? t.library_topic.name : "") : [];
			let uniqueTopicsArray = [...new Set(topicsArray)];
			let topics = uniqueTopicsArray.length ? uniqueTopicsArray.join(", ") : "";
			fido.fetch(`/activities/${activity.id}/report-to-p3`, {
				method: "POST",
				body: JSON.stringify({ report: notesObj, topics })
			}, 'Sharing tip to P3 ...', 'Shared to P3.')
				.then(data => {
					if (data && data.activity && !isJsonEqual(data.activity, activity)) {
						updateActivityHandler(data);
					}
				})
				.catch(error => showSnackbar(error.message));
		}
	};
	console.log("authenticatedUser.accountId", authenticatedUser.accountId);
	return (
		<React.Fragment>
			{authenticatedUser ?
				<ShareActivityForm
					open={shareOpen}
					activityId={activity && activity.id}
					defaultRecipient={defaultRecipient}
					handleClose={handleShareDialogClose}
					handleSubmit={(activityId, values) => handleShareSubmit(activityId, values)} /> : null
			}
			<RootRef rootRef={restoreBannerRef}>
				<RestoreBanner
					show={deleted}
					onEntered={onBannerEntered}
					onExited={onBannerExited}
					flagged={activity ? activity.flagged : false}
					handleRestore={restoreAndSaveHandler} />
			</RootRef>
			<ScrollableArea heightReduction={(restoreBannerRef.current ? restoreBannerRef.current.offsetHeight : 0) + 15}>
				<DeconstructActivity
					activity={activity}
					render={deconstructedProps =>
						<React.Fragment>
							<IndividualActivity
								{...props}
								{...deconstructedProps}
								fidoError={fidoError}
								activity={deconstructedProps}
								isDeleted={deleted}
								saveHandler={save}
								shareHandler={share}
								noActionHandler={noActionNeeded}
								addCommentHandler={addCommentHandler}
								translateHandler={handleTranslateClick}
								rateHandler={handleRateSubmit}
								suppressRateHandler={suppressRateHandler}
								careStatusHandler={careStatusHandler}
								moveStatusHandler={moveStatusHandler}
								showSnackbar={showSnackbar}
								searchTerm={searchTerm} />
							{authenticatedUser ?
								<React.Fragment>
									<Popover
										open={Boolean(shareMenuOpen)}
										anchorEl={shareMenuOpen}
										onClose={handleShareMenuClose}
										anchorOrigin={{
											vertical: 'bottom',
											horizontal: 'center',
										}}
										transformOrigin={{
											vertical: 'top',
											horizontal: 'center',
										}}>
										<MenuItem onClick={() => onShareItemClicked(Constants.ACTIVITY.SHARE_TYPES.EMAIL)}>
											<FontAwesomeIcon className={classes.shareMenuIcon} icon={["fad", "envelope"]} title="email" />
											<Typography>Email</Typography>
										</MenuItem>
										<MenuItem onClick={() => onShareItemClicked(Constants.ACTIVITY.SHARE_TYPES.TEXT_MSG)}>
											<FontAwesomeIcon className={classes.shareMenuIcon} icon={["fad", "comment-dots"]} title="text message" />
											<Typography>Text Message</Typography>
										</MenuItem>

										{AuthorizedUser.hasBTA(authenticatedUser) ?
											<MenuItem
												onClick={() => onShareItemClicked(Constants.ACTIVITY.SHARE_TYPES.REPORT_TO_BTA)}
												disabled={
													deconstructedProps.auditTrail &&
													deconstructedProps.auditTrail.findIndex(item => item.type === Constants.ACTIVITY.AUDIT_TRAIL.REPORT_TO_BTA.ID) > -1}>
												<FontAwesomeIcon className={classes.shareMenuIcon} icon={["fad", "folder-upload"]} title="report to case manager" />
												<Typography>Report to Case Manager</Typography>
											</MenuItem> : null
										}
										{AuthorizedUser.hasP3(authenticatedUser) && authenticatedUser.accountId != 477 ?

											<MenuItem
												onClick={() => onShareItemClicked(Constants.ACTIVITY.SHARE_TYPES.REPORT_TO_P3)}
												disabled={
													deconstructedProps.auditTrail &&
													deconstructedProps.auditTrail.findIndex(item => item.type === Constants.ACTIVITY.AUDIT_TRAIL.REPORT_TO_P3.ID) > -1}>
												<span className={classes.shareMenuIcon}>
													<img
														alt="P3"
														className={classes.shareMenuIcon}
														src={P3Icon} />
												</span>
												<Typography>Share as a tip to P3</Typography>
											</MenuItem> : null
										}
									</Popover>
									<ShareToP3Dialog
										open={shareToP3Open}
										closeHandler={shareToP3}
										activity={deconstructedProps}
										anonymize={activity && activity.extraHeaders && authenticatedUser.ghosting} />
									<ShareToBtaDialog
										open={shareToBtaOpen}
										closeHandler={shareToBta}
										activity={deconstructedProps}
										anonymize={activity && activity.extraHeaders && authenticatedUser.ghosting} />
								</React.Fragment> : null
							}
						</React.Fragment>
					} />
			</ScrollableArea>
		</React.Fragment>
	);
}, (prevProps, newProps) => {
	// if they're the same, don't re-render (return true)
	return isJsonEqual(prevProps, newProps);
});

Activity.displayName = "Activity";

Activity.propTypes = {
	location: PropTypes.object,
	history: PropTypes.object.isRequired,
	showSnackbar: PropTypes.func,
	postActionHandler: PropTypes.func,
	moveStatusHandler: PropTypes.func,
	careStatusHandler: PropTypes.func,
	onAnimateOutHandler: PropTypes.func,
	viewedCallback: PropTypes.func,
	currentStatus: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	showRemoved: PropTypes.bool,
	activity: PropTypes.object,
	fidoError: PropTypes.bool,
	updateActivityHandler: PropTypes.func,
	updateUser: PropTypes.func
};

Activity.whyDidYouRender = Constants.ENV.IS_LOCAL;

export default Activity;
